/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useState, useRef, useEffect } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import Model, { helper } from '@tripian/model';
import DOMPurify from 'dompurify';
import Button from '../../base/Button/Button';
import Web from '../../base/Svg/Icons/Web';
import { ExternalLink, MapPin, Phone2, ChevronLeft, ChevronRight, Left, Right, Star2 } from '../../base/Svg/Icons';
import Flag from '../../base/Svg/Icons/Flag';
import Noodles from '../../base/Svg/Icons/Noodles';
import Restaurant from '../../base/Svg/Icons/Restaurant';
import Features from '../../base/Svg/Icons/Features';
import Feedback from '../../FeedBack/FeedBack';
import PoiActionButton from '../PoiActionButton/PoiActionButton';
import TourCard from '../../TourCard/TourCard';
import PreLoading from '../../base/PreLoading/PreLoading';
import classes from './PoiInfoText.scss';

interface IPoiInfoText {
  poi: Model.Poi;
  hideFeatures?: boolean;
  hideCuisine?: boolean;
  reservationUrl?: string;
  hideBookingButton: boolean;
  hideActionButtons?: boolean;
  replace?: boolean;
  stepOrder?: number;
  addRemoveReplacePoi: (poi: Model.Poi, removeReplaceAdd: number, from?: string, to?: string) => void;
  partOfDay: string;
  reservationButtonClick?: (productId: string, poi: Model.Poi) => void;
  tourTicketProductsLoading?: boolean;
  RESTAURANT_RESERVATION_PROVIDER_IDS: Model.PROVIDER_ID[];
  hideFeedback?: boolean;
  hideTours: boolean;
  loadingFeedback: boolean;
  feedbackSubjects: Model.FeedbackSubjects[];
  tourProducts: (Model.TourSearchProduct & { provider: string })[];
  ticketProducts: (Model.TourSearchProduct & { provider: string })[];
  isBookingClicked?: boolean;
  getTourInfo?: (productId: string, poi: Model.Poi) => void;
  onBookableTabClick?: () => void;
  /** Sonraki sayfa yüklenirken listenin altında gösterilen ek yükleme durumu */
  bookableProductsLoadingMore?: boolean;
  /** Backend'de daha ürün olduğu sürece true; false ise kaydırma tetiklenmez */
  bookableProductsHasMore?: boolean;
  onLoadMoreBookableProducts?: () => void;
  sendFeedback: (value: Model.FeedbackRequest) => Promise<void>;
  t: (value: Model.TranslationKey) => string;
  // Timeline mode props
  plans?: Model.Plan[];
  currentDayIndex?: number;
  onTimelineAdd?: (planId: number, poiId: string, from?: string, to?: string) => void;
}

const PoiInfoText: React.FC<IPoiInfoText> = ({
  poi,
  hideFeatures,
  hideCuisine,
  reservationUrl,
  hideBookingButton = false,
  hideActionButtons = false,
  replace = false,
  stepOrder = -1,
  addRemoveReplacePoi,
  partOfDay,
  reservationButtonClick,
  tourTicketProductsLoading,
  hideFeedback = false,
  loadingFeedback,
  feedbackSubjects,
  tourProducts,
  ticketProducts,
  isBookingClicked = false,
  getTourInfo,
  hideTours,
  sendFeedback,
  onBookableTabClick,
  bookableProductsLoadingMore = false,
  bookableProductsHasMore = false,
  onLoadMoreBookableProducts,
  RESTAURANT_RESERVATION_PROVIDER_IDS,
  t,
  plans,
  currentDayIndex,
  onTimelineAdd,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [activeBookableTab, setActiveBookableTab] = useState<'tickets' | 'tours'>('tickets');
  const [copySuccess, setCopySuccess] = useState('');
  const cuisineSliderRef = useRef<HTMLDivElement>(null);
  const featuresSliderRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [showCuisineNav, setShowCuisineNav] = useState(false);
  const [showFeaturesNav, setShowFeaturesNav] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
  // Sonsuz kaydırma işaretçisi state'te tutuluyor (ref değil): bookable sekmeleri
  // arasında geçerken node unmount/mount olduğu için effect'in yeniden bağlanması gerekir.
  const [bookableSentinelEl, setBookableSentinelEl] = useState<HTMLDivElement | null>(null);

  // Observer callback'i taze değeri ref'ten okur; böylece yükleme durumu değiştiğinde
  // observer yeniden KURULMAZ. Yeniden kurmak, işaretçi hâlâ görünürken anında yeni bir
  // intersection raporu üretip zincirleme isteklere yol açıyordu.
  const bookableLoadingMoreRef = useRef<boolean>(false);
  bookableLoadingMoreRef.current = bookableProductsLoadingMore;

  useEffect(() => {
    if (!bookableSentinelEl || !bookableProductsHasMore || !onLoadMoreBookableProducts) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (bookableLoadingMoreRef.current) return;
        if (entries.some((entry) => entry.isIntersecting)) onLoadMoreBookableProducts();
      },
      // Kullanıcı listenin dibine varmadan önce yüklemeye başla
      { rootMargin: '200px' },
    );
    observer.observe(bookableSentinelEl);
    return () => observer.disconnect();
  }, [bookableSentinelEl, bookableProductsHasMore, onLoadMoreBookableProducts]);

  const isRestaurant = poi.bookings?.some((booking: Model.Booking) => RESTAURANT_RESERVATION_PROVIDER_IDS.includes(booking.providerId)) ?? false;

  const cuisinesArray = poi.cuisines ? poi.cuisines.split(', ').filter((c) => c.trim() !== '') : [];
  const uniqueCuisines: string[] = helper.removeDuplicateValues(cuisinesArray, (s1, s2) => s1 === s2);

  let bookaTable;
  if (!hideBookingButton) {
    // const isBooking = poi.booking.find((provider) => provider.provider_id === Model.PROVIDER_ID.YELP);
    if (isRestaurant) {
      bookaTable = (
        <div className={classes.bookaTable}>
          <Button
            text={reservationUrl ? t('trips.myTrips.itinerary.step.poi.bookATable.viewCancel') : t('trips.myTrips.itinerary.step.poi.bookATable.reserve')}
            color="primary"
            style={{ height: '3rem' }}
            onClick={() => {
              if (reservationButtonClick) {
                // for demo
                reservationButtonClick('rC5mIHMNF5C1Jtpb2obSkA', poi);
              }
            }}
          />
        </div>
      );
    }
  }

  /**
   * MustTries
   */
  let poiMustTries = null;
  if (poi.mustTries && poi.mustTries.length !== 0) {
    let mustTryMessage = '';
    if (poi.mustTries.length === 1) {
      mustTryMessage = `${t('trips.myTrips.itinerary.step.poi.mustTry.message')} ${poi.mustTries[0].name}.`;
    } else {
      mustTryMessage = `${t('trips.myTrips.itinerary.step.poi.mustTry.message')} ${poi.mustTries
        .slice(0, poi.mustTries.length - 1)
        .map((taste) => taste.name)
        .join(', ')} ${t('trips.myTrips.itinerary.step.poi.and')} ${poi.mustTries[poi.mustTries.length - 1].name}.`;
    }

    poiMustTries = (
      <div className={classes.section}>
        <div className={classes.sectionTitle}>
          <Star2 className={classes.icon} />
          <h3>Must Try</h3>
        </div>
        <span className={classes.tag}>{mustTryMessage}</span>
      </div>
    );
  }

  const checkScrollable = (ref: React.RefObject<HTMLDivElement>, setShow: (show: boolean) => void) => {
    if (ref.current) {
      const { scrollWidth, clientWidth } = ref.current;
      setShow(scrollWidth > clientWidth);
    }
  };

  useEffect(() => {
    const checkScrollability = () => {
      checkScrollable(cuisineSliderRef, setShowCuisineNav);
      checkScrollable(featuresSliderRef, setShowFeaturesNav);
    };

    checkScrollability();
    window.addEventListener('resize', checkScrollability);
    return () => window.removeEventListener('resize', checkScrollability);
  }, []);

  // Yalnızca SEÇİLİ alt sekme boşken diğerine geç. Koşulsuz "ticket varsa tickets"
  // demek, sayfalama ile listeler büyüdüğünde (uzunluklar bağımlılıkta olduğu için)
  // kullanıcı Tours'ta gezerken onu Tickets'a geri fırlatıyordu.
  useEffect(() => {
    if (activeTab !== 'bookable') return;
    if (activeBookableTab === 'tickets' && ticketProducts.length === 0 && tourProducts.length > 0) {
      setActiveBookableTab('tours');
    } else if (activeBookableTab === 'tours' && tourProducts.length === 0 && ticketProducts.length > 0) {
      setActiveBookableTab('tickets');
    }
  }, [activeTab, activeBookableTab, ticketProducts.length, tourProducts.length]);

  useEffect(() => {
    if (isBookingClicked && tabsRef.current) {
      setActiveTab('bookable');
      tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isBookingClicked]);

  // Bookable sekmesi açıldığında tüketiciyi haberdar et. Sekme butona basarak da
  // (isBookingClicked ile) Book butonundan programatik olarak da açılabiliyor;
  // bildirimi tek noktada tab durumuna bağlamak ikinci yolun sessizce atlanmasını önler.
  // POI id'ye göre anahtarlı: sekme açıkken POI yerinde değişirse yeni POI için tekrar tetiklenir.
  const bookableTabNotifiedPoiIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeTab !== 'bookable') {
      bookableTabNotifiedPoiIdRef.current = null;
      return;
    }
    if (bookableTabNotifiedPoiIdRef.current === poi.id) return;
    bookableTabNotifiedPoiIdRef.current = poi.id;
    onBookableTabClick?.();
  }, [activeTab, poi.id, onBookableTabClick]);

  const handleScroll = (direction: 'prev' | 'next', ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      const scrollAmount = 200;
      const newScrollLeft = ref.current.scrollLeft + (direction === 'next' ? scrollAmount : -scrollAmount);
      ref.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const copyToClipBoard = async (copyMe: string) => {
    try {
      await navigator.clipboard.writeText(copyMe);
      setCopySuccess(t('trips.myTrips.itinerary.step.poi.address.copied'));
      setTimeout(() => {
        setCopySuccess('');
      }, 1000);
    } catch (err) {
      setCopySuccess(t('trips.myTrips.itinerary.step.poi.address.failed'));
    }
  };

  const getBookableTabButtonClass = (tabType: 'tickets' | 'tours') => {
    const isActive = activeBookableTab === tabType;
    const products = tabType === 'tickets' ? ticketProducts : tourProducts;
    const hasProducts = products.length > 0;

    const buttonClasses = [classes.bookableTab];
    if (isActive) buttonClasses.push(classes.active);
    if (!hasProducts) buttonClasses.push(classes.disabled);

    return buttonClasses.join(' ');
  };

  return (
    <div className={classes.poiInfoText}>
      <div className={classes.headerButtonsDesktop}>
        <div className={classes.contactButtons}>
          {poi.web && (
            <a href={poi.web} target="_blank" rel="noopener noreferrer" className={classes.contactButton}>
              <Web className={classes.icon} />
              <span>{t('trips.myTrips.itinerary.step.poi.web')}</span>
            </a>
          )}
          {poi.address && (
            <div className={classes.contactButton} role="button" tabIndex={0} onClick={() => copyToClipBoard(poi.address)} onKeyPress={() => {}}>
              <MapPin width="16" height="16" className={classes.icon} />
              <span>{copySuccess || t('trips.myTrips.itinerary.step.poi.address.title')}</span>
            </div>
          )}
          {poi.phone && (
            <a href={`tel:${poi.phone}`} className={classes.contactButton}>
              <Phone2 className={classes.icon} />
              <span>{t('trips.myTrips.itinerary.step.poi.phone.call')}</span>
            </a>
          )}
        </div>
        {isRestaurant && !hideBookingButton && bookaTable}
      </div>
      <div className={classes.headerButtonsMobile}>
        <div className={classes.dot} />
        <div className={classes.contactButtonsMobile}>
          {poi.web && (
            <a href={poi.web} target="_blank" rel="noopener noreferrer" className={classes.contactButtonMobile}>
              <div className={classes.contactButtonMobileIcon}>
                <Web className={classes.icon} />
              </div>
              <span>{t('trips.myTrips.itinerary.step.poi.web')}</span>
            </a>
          )}
          {poi.address && (
            <div className={classes.contactButtonMobile} role="button" tabIndex={0} onClick={() => copyToClipBoard(poi.address)} onKeyPress={() => {}}>
              <div className={classes.contactButtonMobileIcon}>
                <MapPin width="16" height="16" className={classes.icon} />
              </div>
              <span>{copySuccess || t('trips.myTrips.itinerary.step.poi.address.title')}</span>
            </div>
          )}
          {poi.phone && (
            <a href={`tel:${poi.phone}`} className={classes.contactButtonMobile}>
              <div className={classes.contactButtonMobileIcon}>
                <Phone2 className={classes.icon} />
              </div>
              <span>{t('trips.myTrips.itinerary.step.poi.phone.call')}</span>
            </a>
          )}
        </div>
        <div className={classes.dot} />
      </div>

      <div className={classes.poiActionContainer}>
        <div>{isRestaurant && !hideBookingButton && bookaTable}</div>
        <PoiActionButton
          poi={poi}
          hideActionButtons={hideActionButtons}
          replace={replace}
          stepOrder={stepOrder}
          addRemoveReplacePoi={addRemoveReplacePoi}
          partOfDay={partOfDay}
          t={t}
          plans={plans}
          currentDayIndex={currentDayIndex}
          onTimelineAdd={onTimelineAdd}
        />
      </div>
      <div className={`hide-m ${classes.dot}`} />

      <div className={classes.tabs} role="tablist" ref={tabsRef}>
        <button className={`${classes.tab} ${activeTab === 'overview' ? classes.active : ''}`} onClick={() => setActiveTab('overview')} role="tab" aria-selected={activeTab === 'overview'} type="button">
          {t('overview.desktop')}
        </button>
        <button
          className={`${classes.tab} ${activeTab === 'bookable' ? classes.active : ''}`}
          /* onBookableTabClick doğrudan burada değil, activeTab'i izleyen effect'te tetiklenir */
          onClick={() => setActiveTab('bookable')}
          role="tab"
          aria-selected={activeTab === 'bookable'}
          type="button"
        >
          {t('bookable.desktop')}
        </button>
      </div>

      {activeTab === 'overview' && (
        <>
          {poi.description && (
            <>
              <div className={classes.description} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(poi.description) }} />
              <div className={classes.divider} />
            </>
          )}

          <div className={classes.contactInfo}>
            {poi.web && (
              <div className={classes.infoItem}>
                <Web className={classes.icon} />
                <div className={classes.content}>
                  <div className={classes.label}>{t('trips.myTrips.itinerary.step.poi.web')}</div>
                  <div className={classes.value}>
                    <a href={poi.web} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>Go to Website</span>
                      <ExternalLink size="14px" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {poi.address && (
              <div className={classes.infoItem}>
                <MapPin width="16" height="16" className={classes.icon} />
                <div className={classes.content}>
                  <div className={classes.label}>{t('trips.myTrips.itinerary.step.poi.address.title')}</div>
                  <div className={classes.value} role="button" tabIndex={0} onClick={() => copyToClipBoard(poi.address)} onKeyPress={() => {}}>
                    {poi.address}
                  </div>
                </div>
              </div>
            )}

            {poi.phone && (
              <div className={classes.infoItem} style={{ paddingBottom: 0 }}>
                <Phone2 className={classes.icon} />
                <div className={classes.content}>
                  <div className={classes.label}>{t('trips.myTrips.itinerary.step.poi.phone.title')}</div>
                  <div className={classes.value}>
                    <a href={`tel:${poi.phone}`}>{poi.phone}</a>
                  </div>
                </div>
              </div>
            )}

            {(poi.web || poi.address || poi.phone) && <div className={classes.divider} />}
          </div>

          {poiMustTries}

          {!hideCuisine && poi.cuisines && uniqueCuisines.length > 0 && (
            <div className={classes.section}>
              <div className={classes.sectionTitle}>
                <Noodles className={classes.icon} />
                <h3>{t('trips.myTrips.itinerary.step.cuisine')}</h3>
              </div>
              <div className={classes.sliderContainer}>
                {showCuisineNav && (
                  <button type="button" className={`${classes.navButton} ${classes.prev}`} onClick={() => handleScroll('prev', cuisineSliderRef)}>
                    <Left />
                  </button>
                )}
                <div className={classes.slider} ref={cuisineSliderRef} onScroll={() => checkScrollable(cuisineSliderRef, setShowCuisineNav)}>
                  {uniqueCuisines.map((cuisine) => (
                    <span key={`cuisine-${cuisine}`} className={classes.tag}>
                      #{cuisine.toLowerCase().replace(/\s+/g, '')}
                    </span>
                  ))}
                </div>
                {showCuisineNav && (
                  <button type="button" className={`${classes.navButton} ${classes.next}`} onClick={() => handleScroll('next', cuisineSliderRef)}>
                    <Right />
                  </button>
                )}
              </div>
            </div>
          )}

          {!hideFeatures && poi.tags && poi.tags.length > 0 && (
            <div className={classes.section}>
              <div className={classes.sectionTitle}>
                <Features className={classes.icon} />
                <h3>{t('trips.myTrips.itinerary.step.features')}</h3>
              </div>
              <div className={classes.sliderContainer}>
                {showFeaturesNav && (
                  <button type="button" className={`${classes.navButton} ${classes.prev}`} onClick={() => handleScroll('prev', featuresSliderRef)}>
                    <ChevronLeft />
                  </button>
                )}
                <div className={classes.slider} ref={featuresSliderRef} onScroll={() => checkScrollable(featuresSliderRef, setShowFeaturesNav)}>
                  {poi.tags.map((tag) => (
                    <span key={`feature-${tag}`} className={classes.tag}>
                      #{tag.toLowerCase().replace(/\s+/g, '')}
                    </span>
                  ))}
                </div>
                {showFeaturesNav && (
                  <button type="button" className={`${classes.navButton} ${classes.next}`} onClick={() => handleScroll('next', featuresSliderRef)}>
                    <ChevronRight />
                  </button>
                )}
              </div>
            </div>
          )}

          {((poi.cuisines && uniqueCuisines.length > 0) || (poi.tags && poi.tags.length > 0)) && <div className={classes.divider} />}

          <div className={classes.reportProblem}>
            <Flag width="16" height="16" className={classes.icon} />
            <div className={classes.content}>
              <div className={classes.label}>{t('trips.myTrips.itinerary.step.poi.reportAProblem')}</div>
              <div className={classes.description}>{t('trips.myTrips.itinerary.step.poi.reportAProblemDescription')}</div>
              <Button text={t('trips.myTrips.itinerary.step.poi.reportAProblemBtnText')} color="primary" className={classes.messageUsNow} onClick={() => setShowFeedbackModal(true)} />
            </div>
          </div>
        </>
      )}
      {!loadingFeedback && !hideFeedback && <Feedback feedbackSubjects={feedbackSubjects} sendFeedback={sendFeedback} showModal={showFeedbackModal} setShowModal={() => setShowFeedbackModal(!showFeedbackModal)} t={t} />}
      {activeTab === 'bookable' && (
        <>
          {tourTicketProductsLoading ? (
            <div className={`${classes.loadingContainer} cursor-not-allowed pointer-events-none`}>
              <PreLoading />
            </div>
          ) : (
            <>
              {/* eslint-disable-next-line no-nested-ternary */}
              {ticketProducts.length === 0 && tourProducts.length === 0 ? (
                /* Tur/bilet yoksa ama restoran rezervasyonu (OpenTable vb.) varsa, Book akışı boş ekranda bitmesin */
                isRestaurant && !hideBookingButton ? (
                  <div className={classes.reserveTableCard}>
                    <div className={classes.reserveTableIconWrapper}>
                      <Restaurant fill="var(--primary-color)" size="26px" />
                    </div>
                    <div className={classes.reserveTableTitle}>{poi.name}</div>
                    {!hideCuisine && uniqueCuisines.length > 0 && <div className={classes.reserveTableSubtitle}>{uniqueCuisines.slice(0, 3).join(' • ')}</div>}
                    <Button
                      text={reservationUrl ? t('trips.myTrips.itinerary.step.poi.bookATable.viewCancel') : t('trips.myTrips.itinerary.step.poi.bookATable.reserve')}
                      color="primary"
                      className={classes.reserveTableButton}
                      onClick={() => {
                        if (reservationButtonClick) {
                          reservationButtonClick('rC5mIHMNF5C1Jtpb2obSkA', poi);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className={classes.noResultsContainer}>
                    <div className={classes.noResultsMessage}>{t('trips.myTrips.itinerary.step.poi.noBookableItems')}</div>
                  </div>
                )
              ) : (
                <>
                  <div className={classes.bookableTabsContainer}>
                    <button type="button" className={getBookableTabButtonClass('tickets')} onClick={() => setActiveBookableTab('tickets')} disabled={ticketProducts.length === 0}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}>
                        <span className={classes.bookableTabText}>{t('poi.bookable.tickets')}</span>
                        <span className={classes.bookableTabDot}>•</span>
                        <span className={classes.bookableTabSubtext}>{t('poi.bookable.covering')}</span>
                      </div>
                    </button>
                    <button type="button" className={getBookableTabButtonClass('tours')} onClick={() => setActiveBookableTab('tours')} disabled={tourProducts.length === 0}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}>
                        <span className={classes.bookableTabText}>{t('poi.bookable.tours')}</span>
                        <span className={classes.bookableTabDot}>•</span>
                        <span className={classes.bookableTabSubtext}>{t('poi.bookable.covering')}</span>
                      </div>
                    </button>
                  </div>
                  {!hideTours && activeBookableTab === 'tickets' && ticketProducts.length > 0 && (
                    <div className="col col12 pb4">
                      {ticketProducts.map((bookingProduct) => (
                        <div key={bookingProduct.id} className="pt4">
                          <TourCard
                            key={bookingProduct.id}
                            title={bookingProduct.title}
                            tags={bookingProduct.tags}
                            images={[bookingProduct.images.find((img) => img.is_cover)?.url || bookingProduct.images[0]?.url || '']}
                            provider={bookingProduct.provider}
                            onBook={() => {
                              if (getTourInfo) getTourInfo(bookingProduct.productId, poi);
                            }}
                            t={t}
                          />
                        </div>
                      ))}
                      {bookableProductsHasMore && <div ref={setBookableSentinelEl} className={classes.bookableSentinel} />}
                      {bookableProductsLoadingMore && (
                        <div className={classes.bookableLoadMoreContainer}>
                          <PreLoading />
                        </div>
                      )}
                    </div>
                  )}

                  {!hideTours && activeBookableTab === 'tours' && tourProducts.length > 0 && (
                    <div className={`col col12 pb4 ${tourTicketProductsLoading ? 'pointer-events-none' : ''}`}>
                      {tourProducts.map((bookingProduct) => (
                        <div key={bookingProduct.id} className="pt4">
                          <TourCard
                            key={bookingProduct.id}
                            title={bookingProduct.title}
                            tags={bookingProduct.tags}
                            images={[bookingProduct.images.find((img) => img.is_cover)?.url || bookingProduct.images[0]?.url || '']}
                            provider={bookingProduct.provider}
                            onBook={() => {
                              if (getTourInfo) getTourInfo(bookingProduct.productId, poi);
                            }}
                            t={t}
                          />
                        </div>
                      ))}
                      {bookableProductsHasMore && <div ref={setBookableSentinelEl} className={classes.bookableSentinel} />}
                      {bookableProductsLoadingMore && (
                        <div className={classes.bookableLoadMoreContainer}>
                          <PreLoading />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PoiInfoText;
